import { useStoreSidebar } from '@lib/store-finder-sidebar'
import { Reducer, useCallback, useEffect, useReducer, useState } from 'react'
import { geocodeByAddress } from 'react-places-autocomplete'

import { INITIAL_STATES } from './constants'
import { MapContainer } from './containers/Map'
import { SearchContainer } from './containers/Search'
import { SidebarContainer } from './containers/Sidebar'
import { TableContainer } from './containers/Table'
import clx from './helpers/clx'
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect'
import useLocalStorage from './hooks/useLocalStorage'
import useMediaQuery from './hooks/useMediaQuery'
import StoreProvider, { StoreContextHandlers } from './provider'
import reducer from './reducer'
import { Action, LatLng, State, StoreItem, SuggestionType, View } from './types'

export function StoreFinderPage(props: State) {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(
    reducer,
    INITIAL_STATES
  )
  const [storeFromStorage, setStorage] = useLocalStorage(
    'store-finder',
    state?.yourStore?.locationObj
  )

  const { setMyStore } = useStoreSidebar()

  useEffect(() => {
    dispatch({
      type: 'INIT',
      payload: props,
    })
  }, [props])

  useIsomorphicLayoutEffect(() => {
    const foundStore = state.stores.find((x) => x?.id === storeFromStorage?.id)
    if (foundStore) {
      dispatch({ type: 'SET_AS_STORE', payload: foundStore })
    }
  }, [storeFromStorage])

  const updateSearchPosition = (results: any[]) => {
    dispatch({
      type: 'SET_SEARCH_LOCATION',
      payload: results,
    })
  }

  const getStoreSorted = useCallback(async (stores: StoreItem[]) => {
    dispatch({
      type: 'START_SORTING_STORES',
      payload: stores,
    })
  }, [])

  const handleChangeView = useCallback((view: View) => {
    dispatch({
      type: 'CHANGE_VIEW',
      payload: view,
    })
  }, [])

  const handleSetCurrentPosition = useCallback((position: LatLng) => {
    dispatch({
      type: 'SET_POSITION',
      payload: position,
    })
  }, [])

  const handleSetStatusCurrentPosition = useCallback(
    (status: boolean) =>
      dispatch({
        type: 'SET_STATUS_POSITION',
        payload: status,
      }),
    []
  )

  const handleSetAsStore = useCallback((store: StoreItem) => {
    dispatch({
      type: 'SET_AS_STORE',
      payload: store,
    })
    setStorage(store.locationObj)
    setMyStore(store.locationObj) // Update sidebar store as well.
    if (store && props?.onSelectStore) props?.onSelectStore(store)
  }, [])

  const handleSetSearchLocation = useCallback(
    async (suggestion: SuggestionType) => {
      dispatch({
        type: 'SET_STATUS_POSITION',
        payload: true,
      })
      const results = await geocodeByAddress(suggestion?.description ?? '')
      if (results.length === 0) return
      updateSearchPosition(results)
    },
    []
  )

  const contextHandlers: StoreContextHandlers = {
    changeView: handleChangeView,
    setCurrentPosition: handleSetCurrentPosition,
    setStatusCurrentPosition: handleSetStatusCurrentPosition,
    setAsStore: handleSetAsStore,
    setSearchLocation: handleSetSearchLocation,
    getStoreSorted,
  }

  const matches = useMediaQuery('(max-width: 768px)')

  const [isMapReady, setMapReady] = useState(false)

  return (
    <StoreProvider.Provider value={[state, contextHandlers]}>
      <div className="flex flex-wrap">
        {!matches && (
          <div className="w-full lg:w-2/3 xl:w-3/5">
            <div className="sticky top-20">
              <MapContainer onReady={() => setMapReady(true)} />
            </div>
          </div>
        )}
        <div className="w-full lg:w-1/3 xl:w-2/5 md:pl-4">
          {isMapReady && <SearchContainer />}
          {matches && (
            <div className="flex justify-between mb-4 w-full bg-gray-200 my-4">
              <button
                className={clx('text-center w-full py-3', {
                  'bg-gray-900 text-white uppercase': state.view === 'list',
                })}
                onClick={() => handleChangeView('list')}
              >
                List
              </button>
              <button
                className={clx('text-center w-full py-3', {
                  'bg-gray-900 text-white uppercase': state.view === 'map',
                })}
                onClick={() => handleChangeView('map')}
              >
                Map
              </button>
            </div>
          )}
          {state.view === 'map' && (
            <div
              style={{
                marginLeft: '-24px',
                marginRight: '-24px',
              }}
            >
              <MapContainer onReady={() => setMapReady(true)} />
            </div>
          )}
          {state.view === 'list' && <SidebarContainer />}
        </div>
      </div>
      {/* <TableContainer /> */}
    </StoreProvider.Provider>
  )
}
